import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/axios';
import { Users, ChevronLeft, ChevronRight, TrendingUp, Wallet, ArrowDownCircle, FileDown } from 'lucide-react';
import ActivityChart from './ActivityChart';
import ExportReportModal from './ExportReportModal';

const monthNames = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const getCurrencyDisplay = (devise) => {
  const symbols = { XOF: 'FCFA', EUR: '€', USD: '$' };
  return symbols[devise] || devise || 'FCFA';
};

const AdminFinances = ({ salonDevise, salonNom, salonId }) => {
  const [period, setPeriod]               = useState('month');
  const [selectedYear, setSelectedYear]   = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [globalStats, setGlobalStats]       = useState({ totalCA: 0, totalDepenses: 0 });
  const [employeeStats, setEmployeeStats]   = useState([]);
  const [chartData, setChartData]           = useState([]);
  const [categoryStats, setCategoryStats]   = useState([]);
  const [paymentStats, setPaymentStats]     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showExport, setShowExport]         = useState(false);

  const currency = getCurrencyDisplay(salonDevise);

  const fetchFinances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/stats/finances', {
        params: { period, year: selectedYear, month: selectedMonth }
      });
      const { global, byEmployee, chartData: apiChartData, categoryStats: cats, paymentStats: pays } = res.data.data;
      setGlobalStats(global || { totalCA: 0, totalDepenses: 0 });
      setEmployeeStats(byEmployee || []);
      setChartData(apiChartData || []);
      setCategoryStats(cats || []);
      setPaymentStats(pays || []);
    } catch (err) {
      console.error('Erreur stats financières:', err);
    } finally {
      setLoading(false);
    }
  }, [period, selectedYear, selectedMonth]);

  useEffect(() => { fetchFinances(); }, [fetchFinances]);

  const chartTitle = useMemo(() => {
    if (period === 'year')  return `Revenus Annuels ${selectedYear}`;
    if (period === 'month') return `Activité de ${monthNames[selectedMonth - 1]} ${selectedYear}`;
    return 'Activité Récente';
  }, [period, selectedMonth, selectedYear]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 space-y-4">
      <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-400 font-bold animate-pulse text-sm">Analyse des finances...</p>
    </div>
  );

  const net        = globalStats.totalCA - globalStats.totalDepenses;
  const isPositive = net >= 0;
  const maxCA      = Math.max(...employeeStats.map(e => e.ca), 1);
  const medals     = ['🥇', '🥈', '🥉'];

  return (
    <div className="space-y-5 pb-6 animate-in fade-in duration-500">

      {showExport && (
        <ExportReportModal
          onClose={() => setShowExport(false)}
          salonNom={salonNom}
          salonDevise={salonDevise}
          salonId={salonId}
        />
      )}

      {/* ── FILTRES ──────────────────────────────────────────── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Période</span>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-xs hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
          >
            <FileDown size={13} /> Exporter PDF
          </button>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-xl">
          {[
            { id: 'day',   label: 'Jour' },
            { id: 'week',  label: 'Sem.' },
            { id: 'month', label: 'Mois' },
            { id: 'year',  label: 'Année' },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-black transition-all ${
                period === p.id
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {period !== 'year' && (
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold outline-none cursor-pointer"
            >
              {monthNames.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          )}
          <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shrink-0">
            <button onClick={() => setSelectedYear(y => y - 1)} className="p-2.5 hover:bg-slate-200 transition-colors">
              <ChevronLeft size={15} />
            </button>
            <span className="px-3 text-sm font-black min-w-[56px] text-center">{selectedYear}</span>
            <button onClick={() => setSelectedYear(y => y + 1)} className="p-2.5 hover:bg-slate-200 transition-colors">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* ── GRAPHIQUE ────────────────────────────────────────── */}
      <ActivityChart
        data={chartData}
        devise={salonDevise}
        displayCurrency={getCurrencyDisplay}
        title={chartTitle}
      />

      {/* ── KPI CARDS ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        {/* CA */}
        <div style={{
          background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)',
          borderRadius: '1.75rem', padding: '28px 24px',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(99,102,241,0.3)',
        }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, left: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp size={17} color="#fff" />
              </div>
              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Chiffre d'Affaires</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#fff', fontSize: 34, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {globalStats.totalCA.toLocaleString()}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 700 }}>{currency}</span>
            </div>
          </div>
        </div>

        {/* Dépenses */}
        <div style={{
          background: '#fff', borderRadius: '1.75rem', padding: '28px 24px',
          position: 'relative', overflow: 'hidden',
          border: '1px solid #fecdd3',
          boxShadow: '0 4px 20px rgba(244,63,94,0.06)',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #f43f5e, #fb7185)', borderRadius: '1.75rem 1.75rem 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowDownCircle size={17} color="#f43f5e" />
            </div>
            <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Dépenses</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: '#0f172a', fontSize: 34, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {globalStats.totalDepenses.toLocaleString()}
            </span>
            <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 700 }}>{currency}</span>
          </div>
        </div>

        {/* Net */}
        <div style={{
          background: '#0b1121', borderRadius: '1.75rem', padding: '28px 24px',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}>
          <div style={{ position: 'absolute', top: -50, right: -50, width: 160, height: 160, borderRadius: '50%', background: isPositive ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: 12, background: isPositive ? 'rgba(16,185,129,0.15)' : 'rgba(244,63,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Wallet size={17} color={isPositive ? '#10b981' : '#f43f5e'} />
              </div>
              <span style={{ color: '#334155', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Résultat Net</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ color: isPositive ? '#10b981' : '#f43f5e', fontSize: 34, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {net.toLocaleString()}
              </span>
              <span style={{ color: '#334155', fontSize: 12, fontWeight: 700 }}>{currency}</span>
            </div>
            <span style={{
              display: 'inline-block',
              color: isPositive ? '#10b981' : '#f43f5e',
              fontSize: 10, fontWeight: 800,
              background: isPositive ? 'rgba(16,185,129,0.12)' : 'rgba(244,63,94,0.12)',
              padding: '4px 10px', borderRadius: 999,
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
              {isPositive ? '▲ Bénéfice' : '▼ Déficit'}
            </span>
          </div>
        </div>
      </div>

      {/* ── LEADERBOARD STAFF ────────────────────────────────── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-slate-50">
          <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
            <Users size={18} className="text-indigo-600" />
          </div>
          <div>
            <h3 className="font-black text-slate-800 text-base leading-none">Performance Staff</h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Classement par CA généré</p>
          </div>
        </div>

        {employeeStats.length > 0 ? (
          <div className="px-4 py-4 space-y-3">
            {employeeStats.map((emp, idx) => {
              const pct    = Math.round((emp.ca / maxCA) * 100);
              const isGold = idx === 0;
              const barColor = idx === 0 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
                             : idx === 1 ? 'linear-gradient(90deg,#818cf8,#a5b4fc)'
                             : '#cbd5e1';
              return (
                <div key={emp._id || emp.id} style={{
                  padding: '14px 16px',
                  background: isGold ? 'linear-gradient(135deg,#fffbeb,#fefce8)' : '#f8fafc',
                  borderRadius: 20,
                  border: isGold ? '1px solid #fde68a' : '1px solid transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 16, minWidth: 22, textAlign: 'center' }}>
                      {medals[idx] ?? `${idx + 1}`}
                    </span>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                      background: isGold ? 'linear-gradient(135deg,#fbbf24,#f59e0b)' : '#e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 900,
                      color: isGold ? '#fff' : '#64748b',
                    }}>
                      {emp.prenom[0]}{emp.nom[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {emp.prenom} {emp.nom}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {emp.nbServices} RDV
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 14 }}>
                        {emp.ca.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{currency}</div>
                    </div>
                  </div>
                  <div style={{ background: '#e2e8f0', borderRadius: 999, height: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${pct}%`,
                      background: barColor, borderRadius: 999,
                      transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center py-12 text-slate-400 font-bold text-sm italic">Aucune donnée trouvée</p>
        )}
      </div>

      {/* ── TOP SERVICES ─────────────────────────────────────── */}
      {categoryStats.length > 0 && (() => {
        const maxCat = Math.max(...categoryStats.map(c => c.ca), 1);
        return (
          <div style={{ background: '#0f172a', borderRadius: '2rem', overflow: 'hidden' }}>
            <div style={{ padding: '22px 24px 10px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Top Services</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontWeight: 700 }}>par CA</span>
            </div>
            {categoryStats.map((cat, idx) => {
              const pct = (cat.ca / maxCat) * 100;
              return (
                <div key={cat.nom} style={{ position: 'relative', padding: '15px 24px', borderTop: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${pct}%`, background: `rgba(255,255,255,${0.07 - idx * 0.012})`, transition: 'width 1.5s cubic-bezier(0.4,0,0.2,1)' }} />
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: 10, fontWeight: 900, color: 'rgba(255,255,255,0.2)', minWidth: 16 }}>{idx + 1}</span>
                    <span style={{ flex: 1, fontWeight: 800, color: '#fff', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.nom}</span>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ fontWeight: 900, color: '#fff', fontSize: 15, letterSpacing: '-0.03em' }}>{cat.ca.toLocaleString()}</span>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 700, marginLeft: 4 }}>{currency}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{ padding: '10px 24px 16px' }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontWeight: 700 }}>{categoryStats.reduce((s, c) => s + c.count, 0)} prestations enregistrées</span>
            </div>
          </div>
        );
      })()}

      {/* ── ENCAISSEMENTS ────────────────────────────────────── */}
      {paymentStats.length > 0 && (() => {
        const totalPay = paymentStats.reduce((s, p) => s + p.total, 0) || 1;
        const modeColors = { 'Especes': '#0f172a', 'Wave': '#2563eb', 'Orange Money': '#ea580c', 'Carte': '#7c3aed' };
        return (
          <div style={{ background: '#fff', borderRadius: '2rem', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            <div style={{ padding: '22px 24px 18px', background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)' }}>
              <p style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 }}>Encaissements</p>
              <p style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.04em', lineHeight: 1 }}>
                {totalPay.toLocaleString()}<span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginLeft: 6 }}>{currency}</span>
              </p>
            </div>
            <div style={{ display: 'flex', height: 5 }}>
              {paymentStats.map(p => (
                <div key={p.mode} style={{ flex: p.total, background: modeColors[p.mode] || '#94a3b8', transition: 'flex 1.5s cubic-bezier(0.4,0,0.2,1)' }} />
              ))}
            </div>
            {paymentStats.map((p, idx) => {
              const pct = Math.round((p.total / totalPay) * 100);
              const color = modeColors[p.mode] || '#94a3b8';
              return (
                <div key={p.mode} style={{ display: 'flex', alignItems: 'center', padding: '14px 24px', borderTop: idx === 0 ? '1px solid #f1f5f9' : '1px solid #f8fafc', gap: 14 }}>
                  <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 999, background: color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 13 }}>{p.mode}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginTop: 2 }}>{p.total.toLocaleString()} {currency}</div>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, color, letterSpacing: '-0.04em', lineHeight: 1 }}>{pct}<span style={{ fontSize: 11, marginLeft: 1 }}>%</span></div>
                </div>
              );
            })}
          </div>
        );
      })()}

    </div>
  );
};

export default AdminFinances;
